import React, { useContext, useEffect, useState } from 'react';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { Button } from 'primereact/button';
import * as Yup from 'yup';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import 'primeflex/primeflex.css';
import { ToastContext } from 'app/context/ToastContext';
import { ProgressSpinner } from 'primereact/progressspinner';
import {
  addContractPayment,
  createAndEvaluateImportedPayment,
  createImportedPayment,
  deletePaymentContract,
  evaluateImportedPayment,
  getImportedPaymentById,
  updateContract,
  updateImportedPayment,
} from 'app/api/paymentApi';
import { formatFromLocalDate } from 'app/shared/util/date-utils';
import DateTextInput from 'app/component/date-text-input';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import ContractWidget from 'app/component/contract-widget';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column'; // Define Yup validation schema

// Define Yup validation schema
const validationSchema = Yup.object({
  contract: Yup.object().required('Toto je povinný údaj'),
  variableSymbol: Yup.string().max(10, 'Max 10 znakov').required('Toto je povinný údaj'),
});

const EditImportedPayment: React.FC = () => {
  const { id } = useParams<{ id?: string }>(); // Capture id from URL

  const [searchParams] = useSearchParams();
  const tempParameters = searchParams.get('p');

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const company = queryParams.get('company_id');

  const navigate = useNavigate();
  const { showSuccess, showError } = useContext(ToastContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [contractPayments, setContractPayments] = useState([]);
  const [showContractPaymentDialog, setShowContractPaymentDialog] = useState(false);
  const [editContract, setEditContract] = useState(null);
  const [deletePaymentDialog, setDeletePaymentDialog] = useState(false);
  const [changedContractId, setChangedContractId] = useState(null);

  function setResponseToData(response) {
    if (response.data) {
      setData(response.data);
      setContractPayments(response.data.contractPayments);
    }
  }

  useEffect(() => {
    if (id) {
      getImportedPaymentById(id)
        .then(response => {
          setResponseToData(response);
        })
        .catch(error => {
          console.error(error);
          showError('Chyba', error.response?.data?.detail);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const paymentType = isOutgoing => {
    return (
      <span>
        {isOutgoing ? (
          <i className="pi pi-arrow-up" style={{ color: 'red', fontWeight: 'bold' }}>
            {' '}
            Odchádzajúca
          </i>
        ) : (
          <i className="pi pi-arrow-down" title="asdf" style={{ color: 'green', fontWeight: 'bold' }}>
            {' '}
            Prichádzajúca
          </i>
        )}
      </span>
    );
  };

  const addOrUpdatePaymentContract = () => {
    setLoading(true);

    let call: Promise<any>;

    if (editContract.id) {
      call = updateContract(editContract.id, editContract);
    } else {
      call = addContractPayment(data.id, editContract);
    }

    call
      .then(response => {
        setResponseToData(response);
        setShowContractPaymentDialog(false);
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const deletePaymentContractFromImportedPayment = () => {
    setLoading(true);

    deletePaymentContract(editContract.id)
      .then(response => {
        setResponseToData(response);
        setDeletePaymentDialog(false);
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const openDialog = contract => {
    setEditContract(contract);
    setShowContractPaymentDialog(true);
  };

  const handleSubmit = values => {
    setLoading(true);

    values.contractPayments = contractPayments;

    let call;

    if (id) {
      call = updateImportedPayment(id, values);
    } else {
      call = createImportedPayment(company, values);
    }

    call
      .then(response => {
        setResponseToData(response);
        showSuccess('Úspech', 'Data úspešne uložené.');
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const onEvaluate = values => {
    setLoading(true);

    let call;

    if (id) {
      call = evaluateImportedPayment(id, values);
    } else {
      call = createAndEvaluateImportedPayment(company, values);
    }

    call
      .then(r => {
        setResponseToData(r);
        showSuccess('Úspech', 'Data úspešne spracované.');
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const confirmDeleteContact = contractToDelete => {
    setEditContract(contractToDelete);
    setDeletePaymentDialog(true);
  };

  const deleteProductDialogFooter = (
    <React.Fragment>
      <Button label="Nie" icon="pi pi-times" outlined onClick={() => setDeletePaymentDialog(false)} />
      <Button label="Áno" icon="pi pi-check" severity="danger" onClick={deletePaymentContractFromImportedPayment} />
    </React.Fragment>
  );

  const actionBodyTemplate = rowData => {
    return (
      <React.Fragment>
        <div className="flex justify-content-around">
          {<Button icon="pi pi-pencil" rounded outlined className="mr-2" onClick={() => openDialog(rowData)} />}
          {<Button icon="pi pi-trash" rounded outlined severity="danger" onClick={() => confirmDeleteContact(rowData)} />}
        </div>
      </React.Fragment>
    );
  };

  const renderHeader = () => {
    return (
      <div className="flex justify-content-between">
        <Button type="button" icon="pi pi-plus" label="Pridať platbu" className="p-button-success" onClick={() => openDialog({})} />
        <div></div>
      </div>
    );
  };

  const header = renderHeader();

  const zaplateneNacasCheckbox = rowData => {
    return <Checkbox style={{ marginLeft: '1rem' }} disabled={true} checked={rowData.zaplateneNacas as boolean} />;
  };

  const showExceedsLimitMessage = () => {
    if (data?.paymentsExceedLimit) {
      return <h5 style={{ color: 'red' }}>!!! Súčet platieb je vyšší ako sa reálne v tejto transakcii dostalo !!!</h5>;
    }
  };

  const dialogFooter = (
    <div>
      <Button
        style={{ backgroundColor: 'gray' }}
        className="p-button-secondary"
        label="Zavrieť"
        icon="pi pi-times"
        onClick={() => setShowContractPaymentDialog(false)}
      />
      <Button
        className="p-button-primary"
        style={{ backgroundColor: '#a92929', marginLeft: '10px' }}
        label="Uložiť"
        onClick={() => addOrUpdatePaymentContract()}
      />
    </div>
  );

  return (
    <>
      <div className="card">
        {loading && (
          <div className="flex justify-content-center">
            <ProgressSpinner />
          </div>
        )}

        {!loading && (
          <>
            <h2>{id ? `Upraviť importovanú platbu (${id})` : 'Vytvoriť novú platbu'}</h2>
            {showExceedsLimitMessage()}
            <Formik
              initialValues={
                data
                  ? data
                  : {
                      variableSymbol: null,
                      contract: null,
                    }
              }
              validationSchema={validationSchema}
              onSubmit={onEvaluate}
            >
              {({ isSubmitting, setFieldValue, values, errors }) => {
                return (
                  <Form onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} className="p-fluid">
                    <div className="grid">
                      <div className="col-6 md:col-3">
                        <div className="p-field">
                          <label htmlFor="processed">Spracovaná platba:</label>
                          <Checkbox
                            style={{ marginLeft: '1rem' }}
                            onChange={e => void setFieldValue(`processed`, e.checked)}
                            checked={values.processed as boolean}
                          />
                        </div>
                      </div>
                      <div className="col-6 md:col-3">
                        <div className="p-field">
                          <label htmlFor="type">Typ platby:</label>
                          <span style={{ marginLeft: '1rem' }}>{paymentType(values.isOutgoing)}</span>
                        </div>
                      </div>
                      <div className="col-6 md:col-3">
                        <div className="p-field">
                          <label htmlFor="ref">Referencia:</label>
                          <span style={{ marginLeft: '0.5rem' }}>
                            <b>{values.ref}</b>
                          </span>
                        </div>
                      </div>
                      <div className="col-6 md:col-3">
                        <div className="p-field">
                          <label htmlFor="date">Prijatá:</label>
                          <span style={{ marginLeft: '0.5rem' }}>
                            <b>{formatFromLocalDate(values.date)}</b>
                          </span>
                        </div>
                      </div>
                      <div className="col-6 md:col-3">
                        <div className="p-field">
                          <label htmlFor="amount">Suma:</label>
                          <span style={{ marginLeft: '0.5rem' }}>
                            <b>
                              {values.amount} {values.currency}
                            </b>
                          </span>
                        </div>
                      </div>
                      <div className="col-6 md:col-3">
                        <div className="p-field">
                          <label htmlFor="account">Účet:</label>
                          <span style={{ marginLeft: '0.5rem' }}>
                            <b>{values.account}</b>
                          </span>
                        </div>
                      </div>
                      <div className="col-12 md:col-6">
                        <div className="p-field">
                          <label htmlFor="accountName">Meno účtu:</label>
                          <span style={{ marginLeft: '0.5rem' }}>
                            <b>{values.accountName}</b>
                          </span>
                        </div>
                      </div>
                      <div className="col-12 md:col-6">
                        <div className="p-field">
                          <label htmlFor="variableSymbol">Variabilný symbol:</label>
                          <Field name="variableSymbol" type="text" className="p-inputtext p-component" />
                          <div className="error-message">
                            <ErrorMessage name="variableSymbol" component="div" />
                          </div>
                        </div>
                      </div>
                      <div className="col-6 md:col-3">
                        <div className="p-field">
                          <label htmlFor="specificSymbol">Špecifický symbol:</label>
                          <Field name="specificSymbol" type="text" className="p-inputtext p-component" />
                          <div className="error-message">
                            <ErrorMessage name="specificSymbol" component="div" />
                          </div>
                        </div>
                      </div>
                      <div className="col-6 md:col-3">
                        <div className="p-field">
                          <label htmlFor="constantSymbol">Konštantný symbol:</label>
                          <Field name="constantSymbol" disabled={true} type="text" className="p-inputtext p-component" />
                          <div className="error-message">
                            <ErrorMessage name="constantSymbol" component="div" />
                          </div>
                        </div>
                      </div>
                      <div className="col-12 md:col-6">
                        <div className="p-field">
                          <label htmlFor="neprerozdelenyZostatok">Neprerozdelený zostatok:</label>
                          <InputNumber
                            id={`neprerozdelenyZostatok`}
                            value={values?.neprerozdelenyZostatok}
                            locale="sk"
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            onValueChange={e => void setFieldValue(`neprerozdelenyZostatok`, e.value)}
                          />
                          <div className="error-message">
                            <ErrorMessage name="neprerozdelenyZostatok" component="div" />
                          </div>
                        </div>
                      </div>
                      <div className="col-12 md:col-6">
                        <div className="p-field">
                          <label htmlFor="recipientMessage">Správa pre príjemcu:</label>
                          <Field name="recipientMessage" disabled={true} type="text" className="p-inputtext p-component" />
                          <div className="error-message">
                            <ErrorMessage name="recipientMessage" component="div" />
                          </div>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="p-field" style={{ marginTop: '1rem' }}>
                          <label htmlFor="contract" />
                          <ContractWidget
                            id="contract"
                            isDeletable={false}
                            value={values.contract?.id}
                            onContractChanged={e => setFieldValue('contract', { id: e })}
                          />
                          <ErrorMessage name="contract" component="div" className="p-error" />
                        </div>
                      </div>

                      <div className="col-12 md:col-6">
                        <div className="p-field">
                          <label htmlFor="warnings">Upozornenia</label>
                          <Field name="warnings" rows={2} as={InputTextarea} autoResize />
                          <ErrorMessage name="warnings" component="div" className="p-error" />
                        </div>
                      </div>
                      <div className="col-12 md:col-6">
                        <div className="p-field">
                          <label htmlFor="errors">Chybné hlásenia</label>
                          <Field name="errors" rows={2} as={InputTextarea} autoResize />
                          <ErrorMessage name="errors" component="div" className="p-error" />
                        </div>
                      </div>
                      <div className="col-4">
                        <Button
                          type="button"
                          label="Späť"
                          style={{ backgroundColor: 'gray' }}
                          className="p-button-secondary"
                          disabled={isSaving}
                          onClick={() => navigate(`/payments?p=${tempParameters}`)}
                        />
                      </div>
                      <div className="col-4">
                        <Button
                          type="button"
                          label="Uložiť"
                          style={{ backgroundColor: '#e31818' }}
                          className="p-button-secondary"
                          disabled={isSaving}
                          onClick={() => handleSubmit(values)}
                        />
                      </div>
                      <div className="col-4">
                        <Button
                          style={{ backgroundColor: '#e31818' }}
                          type="submit"
                          label="Uložiť a spracovať platbu"
                          icon="pi pi-check"
                          className="p-mt-3"
                          disabled={isSaving}
                        />
                      </div>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </>
        )}
        {editContract && (
          <Dialog
            visible={showContractPaymentDialog}
            header="Spracovaná platba"
            style={{ width: '80vw' }}
            footer={dialogFooter}
            onHide={() => setShowContractPaymentDialog(false)}
          >
            {/* InputText for user query */}
            <div className="p-fluid grid">
              <div className="col-4">
                <div className="p-field">
                  <label htmlFor="zaplateneNacas">Zaplatené načas a v správnej výške:</label>
                  <Checkbox
                    style={{ marginLeft: '1rem' }}
                    checked={editContract.zaplateneNacas}
                    onChange={e => setEditContract({ ...editContract, zaplateneNacas: e.checked })}
                  />
                </div>
              </div>
              <div className="col-4">
                <div className="p-field">
                  <label htmlFor="poplatokVopred">Poplatok vopred:</label>
                  <Checkbox
                    style={{ marginLeft: '1rem' }}
                    checked={editContract.poplatokVopred}
                    onChange={e => setEditContract({ ...editContract, poplatokVopred: e.checked })}
                  />
                </div>
              </div>
              <div className="col-4">
                <div className="p-field">
                  <label htmlFor="mimoriadnyVklad">Mimoriadny vklad:</label>
                  <Checkbox
                    style={{ marginLeft: '1rem' }}
                    checked={editContract.mimoriadnyVklad}
                    onChange={e => setEditContract({ ...editContract, mimoriadnyVklad: e.checked })}
                  />
                </div>
              </div>

              <div className="col-4">
                <div className="p-field">
                  <label htmlFor="payedOn">Zaplatené dňa:</label>
                  <DateTextInput
                    id="payedOn"
                    value={editContract.payedOn}
                    onChange={val => setEditContract({ ...editContract, payedOn: val })}
                  />
                </div>
              </div>

              <div className="col-4">
                <div className="p-field">
                  <label htmlFor="periodFrom">Platba platí pre dobu od:</label>
                  <DateTextInput
                    id="periodFrom"
                    value={editContract.periodFrom}
                    onChange={val => setEditContract({ ...editContract, periodFrom: val })}
                  />
                </div>
              </div>
              <div className="col-4">
                <div className="p-field">
                  <label htmlFor="periodTo">Platba platí pre dobu do:</label>
                  <DateTextInput
                    id="periodTo"
                    value={editContract.periodTo}
                    onChange={val => setEditContract({ ...editContract, periodTo: val })}
                  />
                </div>
              </div>
              <div className="col-4">
                <div className="p-field">
                  <label htmlFor="requiredAmount">Požadovaná výška platby:</label>
                  <InputNumber
                    id="requiredAmount"
                    value={editContract.requiredAmount}
                    locale="sk"
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    onValueChange={e => setEditContract({ ...editContract, requiredAmount: e.target.value })}
                  />
                </div>
              </div>
              <div className="col-4">
                <div className="p-field">
                  <label htmlFor="amount">Prijatá suma (bez poplatku):</label>
                  <InputNumber
                    id="amount"
                    value={editContract.amount}
                    locale="sk"
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    onValueChange={e => setEditContract({ ...editContract, amount: e.target.value })}
                  />
                </div>
              </div>
              <div className="col-4">
                <div className="p-field">
                  <label htmlFor="fee">Poplatok:</label>
                  <InputNumber
                    id="fee"
                    value={editContract.fee}
                    locale="sk"
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    onValueChange={e => setEditContract({ ...editContract, fee: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/*<div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>*/}
            {/*  <Button label="Vytvoriť novú osobu" type="button" onClick={() => onOpenCreateModal(null)} size="large" />*/}
            {/*</div>*/}
          </Dialog>
        )}
        <Dialog
          visible={deletePaymentDialog}
          style={{ width: '32rem' }}
          breakpoints={{ '960px': '75vw', '641px': '90vw' }}
          header="Potvrdenie"
          modal
          footer={deleteProductDialogFooter}
          onHide={() => setDeletePaymentDialog(false)}
        >
          <div className="confirmation-content">
            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
            {editContract?.periodFrom && (
              <span>
                Naozaj chcete vymazať priradenú platbu od: <b>{formatFromLocalDate(editContract?.periodFrom)}</b>?
              </span>
            )}
          </div>
        </Dialog>
      </div>
      {data?.contract?.id && (
        <div className="card">
          <div>
            <h4>Spracovaná platba priradená k:</h4>
            {showExceedsLimitMessage()}
            <DataTable
              stripedRows
              value={contractPayments}
              size="small"
              lazy
              responsiveLayout="scroll"
              dataKey="id"
              emptyMessage="Žiadne dáta."
              header={header}
              filterDisplay="menu"
              loading={loading}
              editMode="cell"
              // onRowEditComplete={onRowEditComplete}
              className="table-row-with-line"
            >
              <Column field={'zaplateneNacas'} header="Zaplatené načas" body={v => zaplateneNacasCheckbox(v)} />
              <Column
                field={'periodFrom'}
                header="Od"
                sortable={true}
                body={v => (v?.periodFrom ? formatFromLocalDate(v.periodFrom) : null)}
              />
              <Column field={'periodTo'} header="Do" sortable={true} body={v => (v?.periodTo ? formatFromLocalDate(v.periodTo) : null)} />
              <Column field={'amount'} header="Suma" sortable={true} body={v => v.amount + ' EUR'} />
              <Column field={'fee'} header="Poplatok" sortable={true} body={v => (v.fee ? v.fee : 0) + ' EUR'} />
              <Column header="Akcie" body={actionBodyTemplate}></Column>
            </DataTable>
          </div>
        </div>
      )}
    </>
  );
};

export default EditImportedPayment;
